/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { 
  generateResponsiveBreakpointUrls, 
  createResponsiveBreakpointSrcset,
  isCloudinaryUrl 
} from '../../utils/cloudinary-optimization'

const isProd = (import.meta as any).env.VITE_IS_PRODUCTION === 'true';

export const STRAPI_URL = isProd
  ? (import.meta as any).env.VITE_STRAPI_URL_PROD
  : (import.meta as any).env.VITE_STRAPI_URL_LOCAL;

export const STRAPI_API_KEY = isProd
  ? (import.meta as any).env.VITE_STRAPI_API_KEY_PROD
  : (import.meta as any).env.VITE_STRAPI_API_KEY_LOCAL;

export const GRAPHQL_ENDPOINT = `${STRAPI_URL}/graphql`

// Helper function to get image URL
export const getImageUrl = (image: any): string | null => {
  if (!image) return null
  
  // Handle different possible structures based on the schema
  let url = null
  
  // Direct UploadFile object (from schema: thumb: UploadFile)
  if (image.url) {
    url = image.url
  }
  // Nested under asset (from schema: asset: UploadFile)
  else if (image.asset?.url) {
    url = image.asset.url
  }
  // Legacy structure support
  else if (image.data?.attributes?.url) {
    url = image.data.attributes.url
  }
  
  if (!url) return null
  
  return url.startsWith('http') ? url : `${STRAPI_URL}${url}`
}

// Helper function to build srcset from formats object
export const makeSrcset = (formats: any): string => {
  if (!formats || typeof formats !== 'object') return ''
  
  return Object.values(formats)
    .filter((format: any) => format?.url && format?.width)
    .map((format: any) => {
      const url = format.url.startsWith('http') ? format.url : `${STRAPI_URL}${format.url}`
      return `${url} ${format.width}w`
    })
    .join(', ')
}


// Enhanced image processing with Cloudinary responsive breakpoints
export const processImageWithFormats = (img: any): any => {
  if (!img) return null
  
  const imageUrl = getImageUrl(img)
  
  const processedImg: any = {
    url: imageUrl,
    alt: img.alternativeText || img.asset?.alternativeText || '',
    caption: img.caption || ''
  }
  
  // Generate Cloudinary responsive formats if it's a Cloudinary URL
  if (imageUrl && isCloudinaryUrl(imageUrl)) {
    const responsiveFormats = generateResponsiveBreakpointUrls(imageUrl)
    const responsiveSrcset = createResponsiveBreakpointSrcset(imageUrl)
    
    if (Object.keys(responsiveFormats).length > 0) {
      processedImg.cloudFormats = responsiveFormats
      processedImg.cloudSrcset = responsiveSrcset
    }
  }
  
  // Legacy formats support for older content
  if (img.formats && !processedImg.cloudFormats) {
    processedImg.srcset = makeSrcset(img.formats)
  }
  
  return processedImg
}

// Helper function to process a single image with metadata (legacy support + new responsive)
const processImage = (img: any) => {
  if (!img) return null
  
  // Use enhanced processing if available
  return processImageWithFormats(img) || {
    url: getImageUrl(img),
    alt: img.alternativeText || img.asset?.alternativeText || '',
    caption: img.caption || ''
  }
}

// Helper function to process dynamic zone content
export const processDynamicZone = (items: any[]): any[] => {
  if (!items || !Array.isArray(items)) {
    return []
  }
  
  return items.map(item => {
    const processedItem = { ...item }
    
    // Process various image fields that might be present in blocks
    
    // Standard image fields
    if (item.image) {
      processedItem.image = processImage(item.image)
    }
    
    if (item.media) {
      processedItem.media = processImage(item.media)
    }
    
    // Multiple images array
    if (item.images && Array.isArray(item.images)) {
      processedItem.images = item.images.map(processImage).filter(Boolean)
    }
    
    // Card thumbnails (used in CardsGridBlock)
    if (item.thumb) {
      processedItem.thumb = processImage(item.thumb)
    }
    
    // Map block Image field (capital I)
    if (item.Image) {
      processedItem.Image = processImage(item.Image)
    }
    
    // Process cards array for CardsGridBlock
    if (item.card && Array.isArray(item.card)) {
      processedItem.card = item.card.map((card: any) => ({
        ...card,
        thumb: processImage(card.thumb)
      }))
    }
    
    // Process carousel media structures
    if (item.media?.media && Array.isArray(item.media.media)) {
      processedItem.media = {
        ...item.media,
        media: item.media.media.map((mediaItem: any) => processImage(mediaItem)).filter(Boolean)
      }
    }
    
    // Process nested carousel structure differently if it's structured as { media: { media: [...] } }
    if (item.media && Array.isArray(item.media)) {
      processedItem.media = item.media.map((mediaItem: any) => {
        if (mediaItem.media) {
          return {
            ...mediaItem,
            media: processImage(mediaItem.media)
          }
        }
        return processImage(mediaItem)
      }).filter(Boolean)
    }
    
    return processedItem
  })
} 